import React from "react";
import { motion } from "framer-motion";
import { MaskedLine, FadeUp } from "./MaskedReveal";

const team = [
  {
    name: "Stan Zortman",
    role: "Founding Owner · Chief Flight Instructor",
    creds: "ATP · C408 SkyCourier · Super King Air · CJ4 · Citation XLS",
    ratings: [
      "Flight Instructor / Multi-Engine Instructor",
      "Instrument Flight Instructor / Advanced Ground Instructor",
      "Daily Contract Pilot · Worldwide Delivery Pilot",
    ],
    img: "https://customer-assets-wrfwihn1.emergentagent.net/job_elyon-aviation/artifacts/nvxltysu_5427DE92-D15B-4254-8B84-78E8328CB3B9.PNG",
  },
  {
    name: "Mindy Zortman",
    role: "Founding Owner · Chief Financial Officer",
    creds: "Financial Operations · Client Relations",
    ratings: [],
    img: "https://customer-assets-wrfwihn1.emergentagent.net/job_elyon-aviation/artifacts/7j5nfnvk_ChatGPT%20Image%2019%20jul%202026%2C%2011_23_15.png",
  },
  {
    name: "Brais Gil Zas",
    role: "Pilot · IT Specialist · European Rep",
    creds: "Daily Contract Pilot — United Kingdom",
    ratings: [
      "European-Based Representative",
      "IT & Systems Specialist",
    ],
    img: "https://customer-assets-wrfwihn1.emergentagent.net/job_elyon-aviation/artifacts/vsubca20_PHOTO-2024-10-16-19-07-44.jpg",
  },
];

const letters = [
  {
    quote:
      "Capt. Zortman's mastery of aircraft performance was evident in every lesson. You explained complex systems with such clarity and depth that only comes from years of genuine experience.",
    author: "Liu C — ChinaSky Wings",
    ref: "REF · CN",
  },
  {
    quote:
      "I must commend the El Elyon Air team for organizing such a well-structured course. The material provided was of exceptional quality and covered all relevant aspects of King Air operations. The combination of practical flying exercises in Guatemala and simulator training in Manhattan, Kansas, provided a comprehensive and immersive learning experience.",
    author: "Javier A — Guatemala",
    ref: "REF · GT",
  },
  {
    quote:
      "Captain Zortman distinguishes himself by not only providing controlling skills of this aircraft but also sharing his teaching experiences. Besides, being especially patient with solving any questions or concerns we have and always providing a detailed debriefing and explanation. He goes above and beyond with improving KingAir 360ER SOP and the checklist used here at Executive Aviation Taiwan Corp.",
    author: "Terrence C — Taiwan",
    ref: "REF · TW",
  },
];

export const Team = () => {
  return (
    <section
      id="team"
      className="relative bg-[#050505] py-24 md:py-40 border-t border-white/10"
      data-testid="team-section"
    >
      <div className="mx-auto max-w-[1920px] px-6 md:px-12 lg:px-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-16 md:mb-24">
          <div className="md:col-span-6">
            <FadeUp>
              <div className="font-mono-label text-white/50 mb-6">
                <span className="text-[#FF5A00]">◉</span> Crew · The People
              </div>
            </FadeUp>
            <h2 className="font-display text-5xl md:text-7xl uppercase leading-[0.9] tracking-tight">
              <MaskedLine>Aviators,</MaskedLine>
              <MaskedLine delay={0.15}>
                <span className="font-serif-italic italic text-white/70">
                  not employees.
                </span>
              </MaskedLine>
            </h2>
          </div>
        </div>

        {/* Team grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-12">
          {team.map((m, i) => (
            <motion.div
              key={m.name}
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0, margin: "0px 0px -10% 0px" }}
              transition={{ duration: 1.1, ease: [0.76, 0, 0.24, 1], delay: i * 0.1 }}
              className="md:col-span-1"
              data-testid={`team-member-${i}`}
            >
              <div className="relative corner-frame overflow-hidden spotlight-img aspect-[4/5]">
                <img
                  src={m.img}
                  alt={m.name}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover object-top grayscale hover:grayscale-0 transition-all duration-[1200ms]"
                />
              </div>
              <div className="mt-6 flex items-start justify-between">
                <div>
                  <h3 className="font-display text-2xl md:text-3xl uppercase tracking-tight leading-tight">
                    {m.name}
                  </h3>
                  <div className="font-serif-italic text-white/70 text-lg md:text-xl mt-2">
                    {m.role}
                  </div>
                </div>
                <span className="font-mono-label text-[#FF5A00]">
                  0{i + 1}
                </span>
              </div>
              <p className="mt-4 text-white/70 text-sm leading-relaxed">
                {m.creds}
              </p>
              {m.ratings && m.ratings.length > 0 && (
                <ul className="mt-4 space-y-2 border-l border-white/15 pl-5">
                  {m.ratings.map((r, ri) => (
                    <li
                      key={ri}
                      className="font-mono-label text-white/60 text-[10px] tracking-[0.2em]"
                    >
                      {r}
                    </li>
                  ))}
                </ul>
              )}
            </motion.div>
          ))}
        </div>

        {/* The Meaning Behind Our Name */}
        <div
          className="mt-32 md:mt-48 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12"
          data-testid="meaning-section"
        >
          <div className="md:col-span-5">
            <FadeUp>
              <div className="font-mono-label text-white/50 mb-6">
                <span className="text-[#FF5A00]">◉</span> Origin · The Name
              </div>
            </FadeUp>
            <h2 className="font-display text-4xl md:text-6xl uppercase leading-[0.9] tracking-tight">
              <MaskedLine>The meaning</MaskedLine>
              <MaskedLine delay={0.15}>
                <span className="font-serif-italic italic text-white/70">
                  behind our name.
                </span>
              </MaskedLine>
            </h2>
            <FadeUp delay={0.25}>
              <div className="mt-10 flex items-baseline gap-4">
                <span
                  className="font-display text-6xl md:text-7xl text-white/95 tracking-tight"
                  style={{ fontFeatureSettings: '"liga"' }}
                >
                  אֵל עֶלְיוֹן
                </span>
                <span className="font-mono-label text-white/50">
                  · El Elyon
                </span>
              </div>
            </FadeUp>
          </div>

          <div className="md:col-span-6 md:col-start-7 space-y-8 text-white/75 text-base md:text-lg leading-relaxed">
            <FadeUp>
              <p>
                El Elyon Air is more than a company name — it reflects the
                values that guide every mission we undertake.
              </p>
            </FadeUp>
            <FadeUp delay={0.1}>
              <p>
                <span className="font-serif-italic italic text-white">
                  “El Elyon”
                </span>{" "}
                <span className="text-white/60">(אֵל עֶלְיוֹן)</span> is a Hebrew
                name meaning{" "}
                <span className="text-white">“God Most High.”</span> Throughout
                history, it has represented God's supreme authority,
                faithfulness, protection, and sovereignty. For us, it serves as
                a reminder to pursue excellence with humility, integrity, and a
                commitment to serving others.
              </p>
            </FadeUp>

            <FadeUp delay={0.2}>
              <div className="border-l-2 border-[#FF5A00] pl-6 md:pl-8 py-2">
                <div className="font-mono-label text-white/45 text-xs mb-3">
                  Our motto
                </div>
                <p className="font-serif-italic italic text-3xl md:text-5xl leading-[1.1] text-white">
                  “Fly Higher with El&nbsp;Elyon.”
                </p>
                <p className="mt-4 font-serif-italic text-white/60 text-lg md:text-xl">
                  — a deeper way of saying{" "}
                  <span className="text-white/85">
                    “Fly Higher with God Most High.”
                  </span>
                </p>
              </div>
            </FadeUp>

            <FadeUp delay={0.3}>
              <p>
                Whether we're providing contract pilot services, delivering
                aircraft across the globe, mentoring aircraft owners, or
                training the next generation of professional pilots, we strive
                to operate with{" "}
                <span className="text-white">
                  professionalism, integrity, and purpose
                </span>{" "}
                in everything we do.
              </p>
            </FadeUp>
          </div>
        </div>

        {/* Reference letters */}
        <div className="mt-32 md:mt-48 grid grid-cols-1 md:grid-cols-12 gap-6 mb-12">
          <div className="md:col-span-6">
            <FadeUp>
              <div className="font-mono-label text-white/50 mb-6">
                <span className="text-[#FF5A00]">◉</span> Reference · Letters
                from the Ramp
              </div>
            </FadeUp>
            <h2 className="font-display text-4xl md:text-6xl uppercase leading-[0.9] tracking-tight">
              <MaskedLine>Written</MaskedLine>
              <MaskedLine delay={0.15}>
                <span className="font-serif-italic italic text-white/70">
                  by those who fly with us.
                </span>
              </MaskedLine>
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-10">
          {letters.map((l, i) => (
            <motion.blockquote
              key={i}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 1.1, ease: [0.76, 0, 0.24, 1], delay: i * 0.1 }}
              className="border-l border-white/15 pl-6 md:pl-8"
              data-testid={`reference-letter-${i}`}
            >
              <span className="font-mono-label text-[#FF5A00]/80">
                {l.ref}
              </span>
              <p className="mt-6 font-serif-italic text-2xl md:text-3xl leading-[1.2] text-white/85">
                “{l.quote}”
              </p>
              <footer className="mt-6 font-mono-label text-white/50">
                — {l.author}
              </footer>
            </motion.blockquote>
          ))}
        </div>
      </div>
    </section>
  );
};
